import { shouldPolyfill as shouldPolyfillLocale } from '@formatjs/intl-locale/should-polyfill';
import { shouldPolyfill as shouldPolyfillLocales } from '@formatjs/intl-getcanonicallocales/should-polyfill';
import { shouldPolyfill as shouldPolyfillPluralRules } from '@formatjs/intl-pluralrules/should-polyfill';
import { shouldPolyfill as shouldPolyfillDateTime } from '@formatjs/intl-datetimeformat/should-polyfill';
import { shouldPolyfill as shouldPolyfillNumberFormat } from '@formatjs/intl-numberformat/should-polyfill';

/**
 * The goal of this package is to load polyfills only for browsers that are
 * outdated. For those browsers, we allow a _worse_ experience by blocking
 * the initial page load in order to fetch the polyfills.
 *
 * Many of these polyfills have a load ordering, which should be confirmed in
 * their documentation when performing updates, and then preserved here.
 */
export const loadNeededPolyfills = (): Promise<void> => Promise.all([
	loadCanonicalLocales()
		.then(loadLocale)
		.then(loadPluralRules)
		.then(loadNumberFormat)
		.then(loadDateTimeFormat),
	loadUrlSearchParams(),
	loadProxy(),
])
	.then(() => Promise.resolve());

async function loadProxy() {
	if (!window.Proxy) {
		await import('proxy-polyfill');
	}
}

async function loadLocale() {
	if (shouldPolyfillLocale()) {
		await import('@formatjs/intl-locale/polyfill');
	}
}

async function loadCanonicalLocales() {
	if (!shouldPolyfillLocales()) {
		return;
	}
	await import('@formatjs/intl-getcanonicallocales/polyfill');
}

function loadUrlSearchParams(): Promise<void> {
	return supportsUrlSearchParams()
		? Promise.resolve()
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		: import('core-js/features/url-search-params').then(() => {});
}

function supportsUrlSearchParams(): boolean {
	return (window.URLSearchParams && typeof window.URLSearchParams === 'function');
}

async function loadDateTimeFormat() {
	const unsupportedLocales = ['en-CA', 'fr-CA'].filter(shouldPolyfillDateTime);
	if (unsupportedLocales.length > 0) {
		const jstz = await import('jstz');
		const timezone = jstz.default.determine().name();
		// eslint-disable-next-line no-console
		console.debug(`loading datetime format polyfill for timezone (${timezone}) and locales (${unsupportedLocales})`);
		await import(/* webpackChunkName: "datetimeformat" */ '@formatjs/intl-datetimeformat/polyfill-force');
		await Promise.all([
			import(/* webpackChunkName: "datetimeformat-locale" */ '@formatjs/intl-datetimeformat/add-all-tz'),
			...unsupportedLocales.map((name) => import(/* webpackChunkName: "datetimeformat-locale" */ `@formatjs/intl-datetimeformat/locale-data/${name}.js`)),
		]);
		// When using the polyfill we have to tell it what timezone we're in. This
		// is a bit rough, but should be sufficient for most use cases
		if ('__setDefaultTimeZone' in Intl.DateTimeFormat && timezone) {
			/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
			/* @ts-ignore */
			Intl.DateTimeFormat.__setDefaultTimeZone(timezone); // eslint-disable-line no-underscore-dangle
		}
	}
}

function supportsNumberFormat() {
	return !shouldPolyfillNumberFormat()
		&& window.Intl
		&& window.Intl.NumberFormat
		&& new window.Intl.NumberFormat('en-CA', { notation: 'compact', compactDisplay: 'short' }).format(2300).toLowerCase() === '2.3k';
}

async function loadNumberFormat() {
	// Note that the provided "shouldPolyfill" from @formatjs misses our use case, so we have our own function
	if (supportsNumberFormat()) {
		return;
	}
	await import(/* webpackChunkName: "numberformat" */ '@formatjs/intl-numberformat/polyfill-force');
	Promise.all([
		import(/* webpackChunkName: "numberformat-locale" */ '@formatjs/intl-numberformat/locale-data/en-CA.js'),
		import(/* webpackChunkName: "numberformat-locale" */ '@formatjs/intl-numberformat/locale-data/fr-CA.js'),
	]);
}

async function loadPluralRules() {
	if (!shouldPolyfillPluralRules()) {
		return;
	}
	// Load the polyfill 1st BEFORE loading data
	await import('@formatjs/intl-pluralrules/polyfill');
	await Promise.all([
		import('@formatjs/intl-pluralrules/locale-data/en'),
		import('@formatjs/intl-pluralrules/locale-data/fr'),
	]);
}
